// DropdownFilter.js
import { useState, useEffect, useRef } from "react";
import {
  Col,
  Dropdown,
  FloatingLabel,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import { FieldError } from "react-hook-form";

interface DropdownFilterProps<T> {
  fetchData: (query: string) => Promise<T[]>;
  loading: boolean;
  labelDescription: string;
  labelId?: string;
  getDescription: (item: T) => string;
  getId: (item: T) => number;
  registeredFields: Record<string, any>;
  errorsMessageDescription: FieldError | undefined;
  onSelect?: (item: T | undefined) => void;
  showId?: boolean;
  idColumnWidth?: string;
  idColProps?: { xs?: number; sm?: number; md?: number; lg?: number };
  insert?: boolean;
  notFoundMessage?: string;
}

export function DropdownFilterComponent<T>({
  fetchData,
  loading,
  labelDescription,
  labelId,
  getDescription,
  registeredFields,
  errorsMessageDescription,
  onSelect,
  showId = false,
  idColProps = { xs: 6, sm: 6, md: 6, lg: 6 },
  insert = false,
  notFoundMessage = "Não encontrado",
}: DropdownFilterProps<T>): JSX.Element {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<T[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected] = useState<T>();
  const [hasSelection, setHasSelection] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const {
    onChange: onChangeId,
    onBlur: onBlurId,
    name: nameId,
    ref: refId,
  } = registeredFields["id"];
  const {
    onChange: onChangeDescription,
    onBlur: onBlurDescription,
    name: nameDescription,
    ref: refDescription,
  } = registeredFields["description"];

  useEffect(() => {
    if (inputRef.current && inputRef.current.value.trim() !== "") {
      setHasSelection(true);
    }
  }, []);

  useEffect(() => {
    if (results.length === 1) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [results]);

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    if (hasSelection) {
      return;
    }

    if (query.length >= 3) {
      const timeoutId = setTimeout(() => {
        fetchData(query)
          .then((data) => {
            setResults(data || []);
            setHasSearched(true);
            setShowDropdown(true);
          })
          .catch((error) => {
            console.error("Error fetching data:", error);
            setResults([]);
            setHasSearched(true);
            setShowDropdown(true);
          });
      }, 350);

      setTypingTimeout(timeoutId);
    } else {
      setResults([]);
      setShowDropdown(false);
      setHasSearched(false);
    }

    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [query, hasSelection]);

  const OnInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.target;
    setQuery(value);
    setHasSearched(false);

    if (hasSelection || selected) {
      setHasSelection(false);
      setSelected(undefined);
      onSelect && onSelect(undefined);
      if (inputRef.current) {
        inputRef.current.value = value;
      }
      onChangeDescription(event);
    } else {
      onChangeDescription(event);
    }

    if (value.trim() === "") {
      setSelected(undefined);
      setHasSelection(false);
      setResults([]);
      setShowDropdown(false);
      setHasSearched(false);
      onSelect && onSelect(undefined);
    }
  };

  const OnMenuItemClick = (item: T) => {
    setSelected(item);
    setHasSelection(true);
    const desc = getDescription(item);
    setQuery(desc);
    if (inputRef.current) {
      inputRef.current.value = desc;
    }
    setShowDropdown(false);
    setHasSearched(false);
    onSelect && onSelect(item);
  };

  const OnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (showDropdown && highlightedIndex >= 0 && highlightedIndex < results.length) {
        OnMenuItemClick(results[highlightedIndex]);
      } else if (showDropdown && results.length === 1) {
        OnMenuItemClick(results[0]);
      }
    } else if (event.key === "ArrowDown") {
      if (showDropdown && results.length > 0) {
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev < results.length - 1 ? prev + 1 : 0
        );
      }
    } else if (event.key === "ArrowUp") {
      if (showDropdown && results.length > 0) {
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : results.length - 1
        );
      }
    } else if (event.key === "Escape") {
      setShowDropdown(false);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      setShowDropdown(false);
    }, 200);
    onBlurDescription && onBlurDescription(e);
  };

  const handleFocus = () => {
    if (
      query.length >= 3 &&
      (results.length > 0 || (!loading && hasSearched))
    ) {
      setShowDropdown(true);
    }
  };

  return (
    <Row className="g-2">
      {showId && (
        <Col {...idColProps}>
          <FloatingLabel
            label={labelId}
            className={showId === true ? "" : "d-none"}
          >
            <Form.Control
              onChange={onChangeId}
              onBlur={onBlurId}
              name={nameId}
              ref={refId}
              placeholder={labelId}
              onKeyDown={OnKeyDown}
            ></Form.Control>
          </FloatingLabel>
        </Col>
      )}
      <Col>
        <InputGroup hasValidation>
          <FloatingLabel label={labelDescription}>
            <Form.Control
              placeholder={labelDescription}
              onChange={OnInputChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={OnKeyDown}
              isInvalid={!!errorsMessageDescription?.message}
              name={nameDescription}
              ref={(el: HTMLInputElement | null) => {
                refDescription(el);
                inputRef.current = el;
              }}
            ></Form.Control>

            {query.length >= 3 &&
              showDropdown &&
              (results.length > 0 || (!loading && hasSearched)) && (
                <Dropdown.Menu show={showDropdown}>
                  {results.length > 0 ? (
                    results.map((item, index) => (
                      <Dropdown.Item
                        key={index}
                        active={index === highlightedIndex}
                        onClick={() => OnMenuItemClick(item)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                      >
                        {getDescription(item)}
                      </Dropdown.Item>
                    ))
                  ) : (
                    <Dropdown.Item disabled className="text-muted">
                      {notFoundMessage}
                    </Dropdown.Item>
                  )}
                </Dropdown.Menu>
              )}
            {errorsMessageDescription?.message?.toString() && (
              <Form.Control.Feedback type="invalid">
                {errorsMessageDescription?.message?.toString()}
              </Form.Control.Feedback>
            )}
          </FloatingLabel>
          {loading && (
            <InputGroup.Text>
              <Spinner
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            </InputGroup.Text>
          )}
        </InputGroup>
      </Col>
    </Row>
  );
}
